<template>
    <v-container v-show="selectSearchOnFocus" fluid class="search-results-container">
        <div class="search-results" :style="`left: ${calcLeftOffset()}`">
            <div class="search-results-wrap">
                <div class="search-results-content">
                    <v-card v-if="selectSearchType === 'machines'" elevation-0>
                        <v-list v-model="selectSearchString">
                            <v-list-item
                                v-for="(item, index) in selectMachineGridDataItems"
                                :key="index"
                                color="primary"
                                @click="handleSelect(item.name, 'machines')"
                            >
                                <v-list-item-title class="search-list-machines">
                                    <template v-if="selectSearchString && selectSearchString !== ''">
                                        <template
                                            v-for="(machinesPart, mpi) in highlightedText(
                                                item.name,
                                                selectSearchString
                                            )"
                                            :key="mpi"
                                        >
                                            <span v-if="machinesPart.isMatch" class="text-primary">{{
                                                machinesPart.text
                                            }}</span>
                                            <span v-else>{{ machinesPart.text }}</span>
                                        </template>
                                    </template>
                                    <template v-else>
                                        {{ item.name }}
                                    </template>
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ item.model }}
                                </v-list-item-subtitle>
                                <template #prepend>
                                    <v-icon color="primary" icon="agriculture"></v-icon>
                                </template>
                            </v-list-item>
                            <div v-if="!selectMachineGridDataItems?.length" class="not-found">
                                <v-label>{{ t('label_not_found_machines') }}</v-label>
                            </div>
                            <v-divider class="mt-4 machines-search-list"></v-divider>
                            <!-- JSCPD#21 -->
                            <v-container>
                                <!-- JSCPD#21.1 -->
                                <v-btn
                                    v-if="selectMachineGridDataItems?.length > 1 && selectSearchString?.length"
                                    color="primary"
                                    class="w-100"
                                    :ripple="false"
                                    variant="flat"
                                    @click="handeleShowFor(selectSearchType)"
                                >
                                    {{ t('button_show_all_results_for') }}
                                    {{ selectSearchString }}
                                </v-btn>
                                <v-btn
                                    v-else
                                    color="primary"
                                    :ripple="false"
                                    variant="flat"
                                    class="w-100"
                                    @click="handeleShowAll('machines')"
                                >
                                    {{ t('button_show_all_machines') }}
                                </v-btn>
                            </v-container>
                        </v-list>
                    </v-card>
                    <v-card v-if="selectSearchType === 'implements'" elevation-0>
                        <v-list v-model="selectSearchString">
                            <v-list-item
                                v-for="(item, index) in selectImplementGridDataItems"
                                :key="index"
                                :value="item?.name"
                                color="primary"
                                @click="handleSelect(item.name, 'implements')"
                            >
                                <v-list-item-title class="search-list-implements">
                                    <template v-if="selectSearchString !== '' && selectSearchString">
                                        <template
                                            v-for="(implementsPart, ipi) in highlightedText(
                                                item.name,
                                                selectSearchString
                                            )"
                                            :key="ipi"
                                        >
                                            <span v-if="implementsPart.isMatch" class="text-primary">{{
                                                implementsPart.text
                                            }}</span>
                                            <span v-else>{{ implementsPart.text }}</span>
                                        </template>
                                    </template>
                                    <template v-else>
                                        {{ item.name }}
                                    </template>
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ item.application_type }}
                                </v-list-item-subtitle>
                                <template #prepend>
                                    <v-icon color="primary" icon="build_circle"></v-icon>
                                </template>
                            </v-list-item>
                            <v-divider class="mt-4 implements-search-list"></v-divider>
                            <div v-if="!selectImplementGridDataItems?.length" class="not-found">
                                <v-label>{{ t('label_not_found_implements') }}</v-label>
                            </div>
                            <v-container class="search-cta implements-search-cta">
                                <v-btn
                                    v-if="selectSearchString?.length && selectImplementGridDataItems?.length > 1"
                                    variant="flat"
                                    :ripple="false"
                                    color="primary"
                                    class="w-100"
                                    @click="handeleShowFor(selectSearchType)"
                                >
                                    {{ t('button_show_all_results_for') }}
                                    {{ selectSearchString }}
                                </v-btn>
                                <v-btn
                                    v-else
                                    variant="flat"
                                    :ripple="false"
                                    class="w-100"
                                    color="primary"
                                    @click="handeleShowAll('implements')"
                                >
                                    {{ t('button_show_all_implements') }}
                                </v-btn>
                            </v-container>
                        </v-list>
                    </v-card>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import useUIStore from '../store/ui.store';
import useMachinesStore from '../../machines/store/machine.store';
import useImplementStore from '../../implements/store/implement.store';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { ImplementService } from '../../implements/services/implements.service';
import { MachineService } from '../../machines/services/machine.service';

const { t } = useI18n();
const router = useRouter();
const uiStore = useUIStore();
const machineStore = useMachinesStore();
const implementStore = useImplementStore();

const machineServices = new MachineService();
const implementServices = new ImplementService();

const { selectSearchOnFocus, selectSearchString, selectSearchType } = storeToRefs(uiStore);
const { selectMachineGridDataItems } = storeToRefs(machineStore);
const { selectImplementGridDataItems } = storeToRefs(implementStore);

const handleSelect = (value: string, type: 'machines' | 'implements'): void => {
    machineStore.resetMachineFiltersValues();
    implementStore.resetImplementFiltersValues();
    if (type === 'machines') {
        uiStore.setSearchString(value);
        machineStore.setMachineFiltersValues({
            name: value,
            model: ['All'],
            tractor_loader_only: null,
            min_base_weight: null,
        });
    }
    if (type === 'implements') {
        uiStore.setSearchString(value);
        implementStore.setImplementFiltersValues({
            name: value,
            model: ['All'],
            category: ['All'],
            min_base_weight: null,
            tractor_loader_only: null,
        });
    }
    router.push({
        name: type === 'machines' ? 'MachinesHome' : 'ImplementsHome',
        query: { search: value },
    });
    uiStore.setSearchOnFocus(false);
};

const handeleShowAll = async (type: 'machines' | 'implements'): void => {
    machineStore.resetMachineFiltersValues();
    implementStore.resetImplementFiltersValues();
    router.push({
        name: type === 'machines' ? 'MachinesHome' : 'ImplementsHome',
        query: { search: '' },
    });
    uiStore.setSearchString('');
    uiStore.setSearchOnFocus(false);
    if (selectSearchType.value === 'machines') {
        await machineServices.loadMachineGridData({
            page_num: 1,
            page_size: 10,
            exact_match: 'false',
        });
    }
    if (selectSearchType.value === 'implements') {
        await implementServices.loadImplementGridData({
            page_num: 1,
            page_size: 10,
            exact_match: 'false',
        });
    }
};

const handeleShowFor = (type: 'machines' | 'implements'): void => {
    machineStore.resetMachineFiltersValues();
    implementStore.resetImplementFiltersValues();

    localStorage.setItem(
        'selectMachineGridDataItems',
        type === 'machines'
            ? JSON.stringify(selectMachineGridDataItems.value)
            : JSON.stringify(selectImplementGridDataItems.value)
    );

    router.push({
        name: type === 'machines' ? 'MachinesHome' : 'ImplementsHome',
        query: { search: selectSearchString.value },
    });

    uiStore.setSearchOnFocus(false);
};

const highlightedText = (
    result: string,
    search: string
): {
    text: string;
    isMatch: boolean;
}[] => {
    if (!search) {
        return [{ text: result, isMatch: false }];
    }
    const parts = result.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part) => ({
        text: part,
        isMatch: part.toLowerCase() === search.toLowerCase(),
    }));
};

const calcLeftOffset = (): string => {
    const searchBar: Element | null = document.querySelector('.search-bar');
    if (searchBar) {
        const rect: DOMRect = searchBar.getBoundingClientRect();
        return `${rect.left}px`;
    }
    return '0px';
};
</script>

<style lang="scss">
.search-results-container {
    padding: 0 !important;
    position: relative;
}
.search-results {
    position: absolute;
    z-index: 9999999;
    width: 380px;
    top: -16px;
    &-wrap {
        background-color: transparent;
    }
    &-content {
        .not-found {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 8px;
        }
        .search-cta {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 24px;
        }
    }
}
</style>
